import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/ui/dialog'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/ui/form'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import styles from './EditPipelineModal.module.scss'
import { Button } from '@/ui/button'
import { setPipelineDetails } from '@/features/adp/state/pipelineSlice'
import { useUpdateDataFlowMutation } from '@/features/adp/mutations/usePipelineMutations'
import { closeOverlay } from '@/features/adp/state/overlaySlice'

const editPipelineSchema = z.object({
  name: z.string().min(1, 'Pipeline/Job Name is required'),
  description: z.string().optional(),
})

type EditPipelineFormValues = z.infer<typeof editPipelineSchema>

export function EditPipelineModal() {
  const dispatch = useAppDispatch()
  const { isOpen, type } = useAppSelector(state => state.overlay)
  const { dataFlowId, pipelineName, pipelineDescription } = useAppSelector(state => state.pipeline)
  const updateDataFlowMutation = useUpdateDataFlowMutation()

  const open = isOpen && type === 'edit-pipeline-modal'

  const form = useForm<EditPipelineFormValues>({
    resolver: zodResolver(editPipelineSchema),
    defaultValues: {
      name: pipelineName,
      description: pipelineDescription,
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: pipelineName,
        description: pipelineDescription,
      })
    }
  }, [open, pipelineName, pipelineDescription, form])

  const handleClose = () => {
    if (updateDataFlowMutation.isPending) return
    dispatch(closeOverlay())
  }

  const onSubmit = (values: EditPipelineFormValues) => {
    const nextName = values.name.trim() || pipelineName
    const nextDescription = values.description?.trim() ?? ''

    // Pre-create state: no backend entity yet. Persist locally; the values
    // are picked up by `useFinalizeSourceConfigurationMutation` when the
    // dataflow is created on the first source save.
    if (!dataFlowId) {
      dispatch(
        setPipelineDetails({
          pipelineName: nextName,
          pipelineDescription: nextDescription,
        })
      )
      toast.success('Pipeline details saved')
      dispatch(closeOverlay())
      return
    }

    updateDataFlowMutation.mutate(
      {
        dataFlowId: String(dataFlowId),
        payload: {
          data_flow_name: nextName,
          description: nextDescription,
        },
      },
      {
        onSuccess: () => {
          dispatch(
            setPipelineDetails({
              pipelineName: nextName,
              pipelineDescription: nextDescription,
            })
          )
          toast.success('Pipeline updated')
          dispatch(closeOverlay())
        },
      }
    )
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) handleClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onOpenAutoFocus={event => {
          event.preventDefault()
        }}
      >
        <DialogDescription className={styles.visuallyHidden}>
          Edit pipeline name and description.
        </DialogDescription>

        <div className={styles.header}>
          <DialogTitle className={styles.title}>Edit Title</DialogTitle>
        </div>

        <div className={styles.rule} aria-hidden />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.upperSection}>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="edit-pipeline-name">Pipeline/Job Name</FormLabel>
                    <FormControl>
                      <Input
                        id="edit-pipeline-name"
                        placeholder="Enter your pipeline/job name"
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        id="edit-pipeline-description"
                        placeholder="Enter your pipeline/job description"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className={styles.rule} aria-hidden />

            <div className={styles.footer}>
              <Button
                type="button"
                onClick={handleClose}
                variant="outline"
                disabled={updateDataFlowMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateDataFlowMutation.isPending}>
                {updateDataFlowMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
